/**
 * ChatWindow Component
 * 
 * Displays messages for the selected chat and handles sending messages.
 * Shows real-time message updates with text and audio support.
 */

import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToMessages, sendMessage, markMessagesAsRead } from '../services/db';
import MessageInput from './MessageInput';
import AudioPlayer from './AudioPlayer';

const ChatWindow = ({ chatId, otherUser }) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  // Subscribe to messages
  useEffect(() => {
    if (!chatId) return;

    setLoading(true);
    const unsubscribe = subscribeToMessages(chatId, (updatedMessages) => {
      setMessages(updatedMessages);
      setLoading(false);

      // Mark unread messages as read
      const unreadMessages = updatedMessages
        .filter(msg => msg.senderId !== currentUser.uid && !msg.read)
        .map(msg => msg.id);

      if (unreadMessages.length > 0) {
        markMessagesAsRead(chatId, unreadMessages);
      }
    });

    return unsubscribe;
  }, [chatId, currentUser.uid]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /**
   * Handle sending a message
   */
  const handleSendMessage = async (type, content) => {
    try {
      await sendMessage(chatId, currentUser.uid, type, content);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  /**
   * Format message timestamp
   */
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'h:mm a');
    } catch (error) {
      return '';
    }
  };

  /**
   * Render a single message
   */
  const renderMessage = (message) => {
    const isOwnMessage = message.senderId === currentUser.uid;
    const sender = isOwnMessage ? currentUser : otherUser;

    return (
      <div
        key={message.id}
        className={`flex items-end space-x-2 mb-4 ${
          isOwnMessage ? 'flex-row-reverse space-x-reverse' : ''
        }`}
      >
        {/* Avatar */}
        <img
          src={sender.photoURL || sender.avatarURL || '/default-avatar.png'}
          alt={sender.displayName}
          className="w-8 h-8 rounded-full flex-shrink-0"
        />

        {/* Message bubble */}
        <div
          className={`max-w-xs lg:max-w-md ${
            isOwnMessage ? 'items-end' : 'items-start'
          }`}
        >
          {/* Sender name */}
          {!isOwnMessage && (
            <span className="text-xs text-gray-600 mb-1 block px-3">
              {sender.displayName}
            </span>
          )}

          {/* Message content */}
          <div
            className={`rounded-2xl px-4 py-2 ${
              isOwnMessage
                ? 'bg-primary-500 text-white rounded-br-none'
                : 'bg-gray-200 text-gray-900 rounded-bl-none'
            }`}
          >
            {message.type === 'text' ? (
              <p className="text-sm break-words">{message.content}</p>
            ) : (
              <AudioPlayer audioUrl={message.content} isOwnMessage={isOwnMessage} />
            )}
          </div>

          {/* Timestamp */}
          <span className="text-xs text-gray-500 mt-1 block px-3">
            {formatMessageTime(message.timestamp)}
            {isOwnMessage && message.read && (
              <span className="ml-1">✓✓</span>
            )}
          </span>
        </div>
      </div>
    );
  };

  if (!chatId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <div className="text-6xl mb-4">💬</div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          Select a chat to start messaging
        </h3>
        <p className="text-gray-600">
          Choose a conversation from the list or search for a user
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Chat header */}
      <div className="flex items-center space-x-3 p-4 border-b border-gray-200 bg-white">
        <img
          src={otherUser?.avatarURL || '/default-avatar.png'}
          alt={otherUser?.displayName}
          className="w-10 h-10 rounded-full"
        />
        <div className="flex-1">
          <h2 className="font-semibold text-gray-900">
            {otherUser?.displayName}
          </h2>
          <p className="text-xs text-gray-500">{otherUser?.verifiedTag}</p>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="text-4xl mb-2">👋</div>
            <p className="text-gray-600">
              No messages yet. Start the conversation!
            </p>
          </div>
        ) : (
          <>
            {messages.map(renderMessage)}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Message input */}
      <MessageInput onSendMessage={handleSendMessage} />
    </div>
  );
};

ChatWindow.propTypes = {
  chatId: PropTypes.string,
  otherUser: PropTypes.object,
};

export default ChatWindow;
